import { ChevronDownIcon } from 'lucide-react';
import { AsYouType, type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib/utils';
import {
  DEFAULT_PHONE_COUNTRY,
  countryFlag,
  getPhoneCountries,
} from '../../../lib/phone-countries';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export type PhoneNumberInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'type' | 'value'
> & {
  value: string | undefined;
  onChange: (value: string) => void;
  /** The field's allowed countries; exactly one locks the selector to it. */
  countries?: CountryCode[];
};

/** What the national part shows: the number without its dial code, or the raw value if unparseable. */
const toNationalText = (
  value: string | undefined,
  country: CountryCode,
  callingCode: string,
): string => {
  if (!value) return '';

  // A value stored in national form ("06 10 10 10 10") would otherwise show its trunk digit
  // next to the dial code; the library's own grouping drops it.
  const parsed = parsePhoneNumberFromString(value, country);
  if (parsed?.isValid() && parsed.country === country) {
    return parsed
      .formatInternational()
      .slice(callingCode.length + 1)
      .trim();
  }

  const prefix = `+${callingCode}`;
  return value.startsWith(prefix) ? value.slice(prefix.length).trimStart() : value;
};

export const PhoneNumberInput = React.forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  ({ value, onChange, countries, disabled, ...props }, ref) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    // Only consulted while the value carries no country of its own — an empty or partial number.
    const [pickedCountry, setPickedCountry] = React.useState<CountryCode>();

    const lockedCountry = countries?.length === 1 ? countries[0] : undefined;
    const allCountries = getPhoneCountries(i18n.language);
    const selectable = countries?.length
      ? allCountries.filter(c => countries.includes(c.code))
      : allCountries;

    const country =
      lockedCountry ??
      parsePhoneNumberFromString(value ?? '')?.country ??
      pickedCountry ??
      // Never a country the field forbids: the picker would not even list it.
      (countries?.length ? countries[0] : DEFAULT_PHONE_COUNTRY);
    const callingCode = allCountries.find(c => c.code === country)?.callingCode ?? '33';
    const nationalText = toNationalText(value, country, callingCode);

    const emit = (nationalInput: string, isDeletion: boolean) => {
      // AsYouType re-inserts the separator a backspace just removed, so a deletion is never formatted.
      const formatter = new AsYouType(country);
      const formatted = isDeletion ? nationalInput : formatter.input(nationalInput);
      const e164 = isDeletion ? undefined : formatter.getNumberValue();
      onChange(e164 ?? (formatted ? `+${callingCode} ${formatted}` : ''));
    };

    const pickCountry = (next: CountryCode) => {
      setPickedCountry(next);
      setOpen(false);
      setSearch('');
      const digits = nationalText.replace(/\D/g, '');
      const nextCallingCode = allCountries.find(c => c.code === next)?.callingCode ?? '';
      onChange(digits ? `+${nextCallingCode} ${new AsYouType(next).input(digits)}` : '');
    };

    const filtered = search
      ? selectable.filter(
          c =>
            c.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
            c.callingCode.startsWith(search.replace('+', '')),
        )
      : selectable;

    return (
      <Group
        gap={0}
        className={cn(
          'h-10 w-full rounded-lg border border-input bg-background shadow-sm transition-all',
          'ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:border-primary focus-within:rounded-full',
          disabled && 'cursor-not-allowed bg-gray-50',
        )}
      >
        {lockedCountry ? (
          <Text
            inline
            size="lg"
            className="flex select-none items-center pl-3 leading-none"
            aria-label={allCountries.find(c => c.code === lockedCountry)?.label}
          >
            {countryFlag(lockedCountry)}
          </Text>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild disabled={disabled}>
              <button
                type="button"
                aria-label={t('phoneNumberInput.selectCountry')}
                className="flex items-center gap-1 rounded-l-lg pl-3 pr-0.5 text-lg leading-none outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
              >
                {countryFlag(country)}
                <ChevronDownIcon className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={t('phoneNumberInput.searchCountry')}
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList>
                  <CommandEmpty>{t('phoneNumberInput.noCountry')}</CommandEmpty>
                  {filtered.map(({ code, label, callingCode: dial }) => (
                    <CommandItem key={code} value={code} onSelect={() => pickCountry(code)}>
                      <Text inline size="lg" className="mr-2 leading-none">
                        {countryFlag(code)}
                      </Text>
                      <Text inline truncate className="flex-1">
                        {label}
                      </Text>
                      <Text inline tone="muted" className="ml-2 shrink-0">
                        +{dial}
                      </Text>
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        <Text inline size="md" tone="muted" className="select-none pl-1.5 pr-1 md:text-sm">
          (+{callingCode})
        </Text>

        <input
          {...props}
          ref={ref}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          disabled={disabled}
          value={nationalText}
          onChange={event => {
            const inputType =
              'inputType' in event.nativeEvent && typeof event.nativeEvent.inputType === 'string'
                ? event.nativeEvent.inputType
                : undefined;
            const isDeletion = inputType
              ? inputType.startsWith('delete')
              : event.target.value.length <= nationalText.length;
            emit(event.target.value, isDeletion);
          }}
          className="h-full min-w-0 flex-1 rounded-r-lg bg-transparent pr-3 text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed md:text-sm"
        />
      </Group>
    );
  },
);
PhoneNumberInput.displayName = 'PhoneNumberInput';
